/**
 * Formulário de um dia da escala, dentro da caixa que o clique na célula abre.
 *
 * Papel: marca o estado do dia em `Chip` — "Normal", as quatro situações e o
 * atalho "Família + Todos" —, põe cada pessoa da escala em plantão ou em folga
 * por dois grupos de `Chip`, preenche os dois grupos pela escala padrão do dia
 * da semana em "↺ Escala padrão", guarda o texto livre de quem passou pela
 * casa, as observações e as marcas de previsão e de a definir, avisa das
 * pendências da escala e oferece "Limpar dia".
 *
 * Como usar: quem monta este componente passa a data e o dia já carregado, ou
 * `null` para um dia ainda em branco, e deverá dar a ele uma `key` diferente por
 * data. Sem a `key`, o React reaproveita a mesma instância ao trocar de dia e os
 * campos continuam com o que a pessoa via antes. O título da caixa é de quem a
 * monta.
 *
 * Cada pessoa tem três estados e não dois: plantão, folga e nenhum. O terceiro é
 * o que permite salvar um dia pela metade, e é ele que faz o aviso de situação
 * em falta aparecer em vez de o dia ser recusado. Escolher alguém no grupo de
 * folga tira a pessoa do plantão, e o contrário também: a mesma pessoa nos dois
 * papéis é o que o banco recusa, e os dois grupos tornam impossível montá-lo.
 *
 * "Normal" limpa todas as situações, e "Família + Todos" deixa exatamente as
 * duas, desmarcando folga geral e feriado. As outras quatro ligam e desligam só
 * a si mesmas.
 *
 * "↺ Escala padrão" troca as duas listas inteiras pelo padrão de
 * `escalaPadrao`, e a situação do dia só quando o padrão tem uma; no dia da
 * semana sem padrão, o botão diz isso e não mexe em nada. A linha "Padrão: …"
 * debaixo da folga mostra o que o botão faria antes de clicar.
 *
 * Os avisos de pendência vêm de `pendenciasDaEscala`, recalculados a cada
 * mudança. Com pendência crítica, "Salvar" pergunta antes — "Deseja salvar
 * mesmo assim?" —, e o dia é gravado se a resposta for sim. "Limpar dia" apaga o registro inteiro
 * sem pedir confirmação, porque o dia continua à vista atrás da caixa e se
 * remonta pela mesma caixa; num dia ainda em branco, ele só fecha a caixa.
 */
import {
  type Ausencia,
  type DiaCalendario,
  type EscalaPadraoDoDia,
  type PapelNoDia,
  type StatusDiaCalendario,
  feriadoDoDia,
  novoDiaCalendarioSchema,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm, useWatch } from 'react-hook-form';

import { ErroDaApi } from '@/shared/api';
import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { cn } from '@/shared/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Chip } from '@/shared/ui/chip';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  aplicarEscalaPadrao,
  fraseDaPendencia,
  linhaDaEscalaPadrao,
  nomeNaEscala,
  pendenciasDaEscala,
} from '../escala';
import { useExcluirDia, useSalvarDia } from '../hooks/usar-calendario';
import { type DiaNoFormulario, type PessoaDaEscala } from '../types';

interface FormularioDiaProps {
  /** A data do dia, em `AAAA-MM-DD`. */
  dia: string;
  residenciaId: string;
  /** O dia já gravado, ou `null` quando ainda não há registro nenhum nele. */
  registro: DiaCalendario | null;
  /** Quem está na escala da casa, que é quem pode ser escalado. */
  pessoas: PessoaDaEscala[];
  /** As ausências que cobrem este dia, para o aviso de férias na escala. */
  ausencias: Ausencia[];
  /** A escala padrão do dia da semana desta data, ou `undefined` sem padrão. */
  escalaPadrao: EscalaPadraoDoDia | undefined;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<DiaNoFormulario> | undefined> = {
  status: 'status',
  escalados: 'escalados',
  extras: 'extras',
  observacao: 'observacao',
  previsao: 'previsao',
  aDefinir: 'aDefinir',
  residenciaId: 'residenciaId',
  dia: 'dia',
};

/** Os `Chip` do estado do dia, na ordem em que aparecem. */
const ESTADOS_DO_DIA = [
  { chave: 'normal', rotulo: 'Normal' },
  { chave: 'familia', rotulo: 'Família' },
  { chave: 'todos_residencia', rotulo: 'Todos na residência' },
  { chave: 'familia-e-todos', rotulo: 'Família + Todos' },
  { chave: 'folga_total', rotulo: 'Folga TODOS' },
  { chave: 'feriado', rotulo: 'Feriado' },
] as const;

type EstadoDoDia = (typeof ESTADOS_DO_DIA)[number]['chave'];

/**
 * Se um `Chip` de estado está aceso para as situações marcadas.
 *
 * @param estado - O `Chip` perguntado.
 * @param status - As situações marcadas no formulário.
 * @returns `true` para "Normal" sem situação nenhuma, para "Família + Todos"
 *   com as duas marcadas, e para as demais quando a própria está marcada.
 */
function estadoAceso(estado: EstadoDoDia, status: readonly StatusDiaCalendario[]): boolean {
  if (estado === 'normal') return status.length === 0;
  if (estado === 'familia-e-todos') {
    return status.includes('familia') && status.includes('todos_residencia');
  }
  return status.includes(estado);
}

/** O `Label` de cada grupo da caixa, com o espaço até o grupo. */
const ROTULO_DE_GRUPO = 'mb-2 block';

/** Cada grupo da caixa, com o espaço até o seguinte. */
const GRUPO = 'mb-4';

/**
 * O formulário de um dia.
 *
 * @param dia - A data editada, que não muda enquanto a caixa está aberta.
 * @param residenciaId - A casa do calendário aberto.
 * @param registro - O que já estava gravado no dia, ou `null`.
 * @param pessoas - Quem está na escala da casa, na ordem da coluna; quem já
 *   estava escalado no dia e não está aqui ganha o seu `Chip` depois deles.
 * @param ausencias - As ausências que cobrem o dia.
 * @param escalaPadrao - O padrão do dia da semana, que "↺ Escala padrão"
 *   aplica e a linha "Padrão: …" descreve.
 * @param onSalvo - Chamado depois de gravar ou de limpar, para fechar a caixa.
 * @param onCancelar - Chamado ao desistir, sem gravar nada.
 * @returns O formulário do dia, com os avisos de pendência antes dos botões.
 */
export function FormularioDia({
  dia,
  residenciaId,
  registro,
  pessoas,
  ausencias,
  escalaPadrao,
  onSalvo,
  onCancelar,
}: FormularioDiaProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [semPadrao, setSemPadrao] = useState(false);
  const [aConfirmar, setAConfirmar] = useState<DiaNoFormulario | null>(null);
  const salvar = useSalvarDia();
  const limpar = useExcluirDia();

  const form = useForm<DiaNoFormulario>({
    resolver: zodResolver(novoDiaCalendarioSchema),
    defaultValues: {
      residenciaId,
      dia,
      status: registro?.status ?? [],
      escalados: (registro?.escalados ?? []).map((pessoa) => ({
        colaboradorId: pessoa.colaboradorId,
        papel: pessoa.papel,
      })),
      extras: registro?.extras ?? '',
      observacao: registro?.observacao ?? '',
      previsao: registro?.previsao ?? false,
      aDefinir: registro?.aDefinir ?? false,
    },
  });

  const status = useWatch({ control: form.control, name: 'status' }) ?? [];
  const escalados = useWatch({ control: form.control, name: 'escalados' }) ?? [];

  const pendencias = pendenciasDaEscala({
    status,
    escalados,
    deFerias: ausencias.map((a) => a.colaborador.id),
    daCasa: pessoas.map((p) => p.id),
  });
  // Quem já estava escalado no dia e não está mais em `pessoas` — desligado
  // depois dele — continua com o seu `Chip`, para a escala gravada não sumir
  // da caixa enquanto continua valendo no dia.
  const naEscala = [
    ...pessoas.map((p) => ({ id: p.id, nome: nomeNaEscala(p) })),
    ...(registro?.escalados ?? [])
      .filter((e) => !pessoas.some((p) => p.id === e.colaboradorId))
      .map((e) => ({ id: e.colaboradorId, nome: e.apelido ?? e.nomeCompleto })),
  ];
  const nomePorId = new Map(naEscala.map((p) => [p.id, p.nome]));
  const feriado = feriadoDoDia(dia);
  const ocupado = salvar.isPending || limpar.isPending;

  /** Aplica o `Chip` de estado clicado às situações marcadas. */
  const handleEscolherEstado = (estado: EstadoDoDia) => {
    const marcadas: StatusDiaCalendario[] =
      estado === 'normal'
        ? []
        : estado === 'familia-e-todos'
          ? ['familia', 'todos_residencia']
          : status.includes(estado)
            ? status.filter((s) => s !== estado)
            : [...status, estado];
    form.setValue('status', marcadas, { shouldDirty: true });
  };

  /** Põe uma pessoa num papel, ou a tira da escala quando já estava nele. */
  const handleDefinirPapel = (colaboradorId: string, papel: PapelNoDia) => {
    const semEla = escalados.filter((p) => p.colaboradorId !== colaboradorId);
    const atual = escalados.find((p) => p.colaboradorId === colaboradorId);
    const proximos = atual?.papel === papel ? semEla : [...semEla, { colaboradorId, papel }];
    form.setValue('escalados', proximos, { shouldDirty: true });
  };

  /** Preenche as situações e a escala pelo padrão do dia da semana. */
  const handleAplicarPadrao = () => {
    if (escalaPadrao === undefined) {
      setSemPadrao(true);
      return;
    }
    setSemPadrao(false);
    const aplicado = aplicarEscalaPadrao(escalaPadrao, pessoas, status);
    form.setValue('status', aplicado.status, { shouldDirty: true });
    form.setValue('escalados', aplicado.escalados, { shouldDirty: true });
  };

  /** Apaga o registro do dia e fecha a caixa, se a API aceitar. */
  async function handleLimpar() {
    setErroGeral(null);
    if (registro === null) {
      onSalvo();
      return;
    }
    try {
      await limpar.mutateAsync(registro.id);
      onSalvo();
    } catch (erro) {
      setErroGeral(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A limpeza falhou antes de chegar ao servidor.',
      );
    }
  }

  /** Grava o dia e fecha a caixa, ou mostra o que a API recusou. */
  async function gravar(dados: DiaNoFormulario) {
    setErroGeral(null);
    try {
      await salvar.mutateAsync(novoDiaCalendarioSchema.parse(dados));
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  }

  const criticas = pendencias.filter((pendencia) => pendencia.critico);

  const handleSalvar = form.handleSubmit(async (dados) => {
    if (criticas.length > 0) {
      setAConfirmar(dados);
      return;
    }
    await gravar(dados);
  });

  /** Grava o dia que esperava a confirmação das pendências críticas. */
  async function handleConfirmarSalvar() {
    if (aConfirmar === null) return;
    const dados = aConfirmar;
    setAConfirmar(null);
    await gravar(dados);
  }

  const deFeriasNoDia = ausencias.map((a) => nomeNaEscala(a.colaborador));
  const linhaDoPadrao = linhaDaEscalaPadrao(escalaPadrao, pessoas);

  return (
    <form onSubmit={(evento) => void handleSalvar(evento)}>
      {feriado !== null && (
        <p className="-mt-3 mb-4 font-mono text-[11px] text-perigo">{feriado}</p>
      )}

      <div role="group" aria-labelledby="dia-estado" className={GRUPO}>
        <Label id="dia-estado" className={ROTULO_DE_GRUPO}>
          Estado do dia
        </Label>
        <div className="flex flex-wrap gap-1.5">
          {ESTADOS_DO_DIA.map((estado) => (
            <Chip
              key={estado.chave}
              ativo={estadoAceso(estado.chave, status)}
              tom={estado.chave === 'feriado' ? 'perigo' : 'padrao'}
              onClick={() => handleEscolherEstado(estado.chave)}
            >
              {estado.rotulo}
            </Chip>
          ))}
        </div>
      </div>

      {(['plantao', 'folga'] as const).map((papel) => (
        <div key={papel} role="group" aria-labelledby={`dia-${papel}`} className={GRUPO}>
          {papel === 'plantao' ? (
            <Label id={`dia-${papel}`} className={ROTULO_DE_GRUPO}>
              Plantão (Res:)
            </Label>
          ) : (
            <div className="mb-2 flex items-center justify-between gap-2">
              <Label id={`dia-${papel}`}>Folga</Label>
              <Button
                type="button"
                variant="outline"
                size="xs"
                className="px-2.5 py-1 text-[11px]"
                onClick={handleAplicarPadrao}
              >
                ↺ Escala padrão
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {naEscala.map((pessoa) => (
              <Chip
                key={pessoa.id}
                ativo={escalados.some(
                  (p) => p.colaboradorId === pessoa.id && p.papel === papel,
                )}
                onClick={() => handleDefinirPapel(pessoa.id, papel)}
              >
                {pessoa.nome}
              </Chip>
            ))}
          </div>
          {papel === 'folga' && semPadrao && (
            <p role="alert" className="mt-1.5 text-[11px] text-perigo">
              Sem escala padrão para este dia da semana
            </p>
          )}
          {papel === 'folga' && linhaDoPadrao !== null && (
            <p className="mt-1.5 text-[11px] text-texto-fraco italic">{linhaDoPadrao}</p>
          )}
          {papel === 'folga' && deFeriasNoDia.length > 0 && (
            <p className="mt-1.5 text-[11px] text-texto-fraco italic">
              De férias neste dia: {deFeriasNoDia.join(', ')}
            </p>
          )}
        </div>
      ))}

      <div className={GRUPO}>
        <Label htmlFor="extras" className={ROTULO_DE_GRUPO}>
          Quem mais passou pela casa
        </Label>
        <Controller
          control={form.control}
          name="extras"
          render={({ field }) => <Input id="extras" {...field} value={field.value ?? ''} />}
        />
      </div>

      <div className="mb-2">
        <Label htmlFor="observacao" className={ROTULO_DE_GRUPO}>
          Observações
        </Label>
        <Controller
          control={form.control}
          name="observacao"
          render={({ field }) => (
            <Textarea id="observacao" rows={3} {...field} value={field.value ?? ''} />
          )}
        />
      </div>

      <div className="mb-2 flex flex-col gap-1.5 text-[13px]">
        <Controller
          control={form.control}
          name="previsao"
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id="dia-previsao"
                checked={field.value ?? false}
                onCheckedChange={(marcado) => field.onChange(marcado === true)}
              />
              <Label
                htmlFor="dia-previsao"
                className="cursor-pointer font-sans text-[13px] leading-normal font-normal tracking-normal text-foreground normal-case"
              >
                <span>
                  Marcar como <strong>Previsão</strong> (não confirmado)
                </span>
              </Label>
            </div>
          )}
        />
        <Controller
          control={form.control}
          name="aDefinir"
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id="dia-aDefinir"
                checked={field.value ?? false}
                onCheckedChange={(marcado) => field.onChange(marcado === true)}
              />
              <Label
                htmlFor="dia-aDefinir"
                className="cursor-pointer font-sans text-[13px] leading-normal font-normal tracking-normal text-foreground normal-case"
              >
                <span>
                  Marcar como <strong>A definir</strong>
                </span>
              </Label>
            </div>
          )}
        />
      </div>

      {pendencias.length > 0 && (
        <div className="mt-3.5 rounded-[4px] border border-aviso bg-aviso/8 px-3.5 py-2.5 text-xs text-texto-medio">
          <span className="mb-1.5 block font-mono text-[10px] font-semibold tracking-[0.05em] text-aviso uppercase">
            ⚠ Atenção — conflitos detectados
          </span>
          <ul className="m-0 list-disc pl-4">
            {pendencias.map((pendencia) => (
              <li
                key={`${pendencia.colaboradorId}-${pendencia.tipo}`}
                className={cn(pendencia.critico && 'text-perigo')}
              >
                <strong>{nomePorId.get(pendencia.colaboradorId) ?? '—'}</strong>{' '}
                {fraseDaPendencia(pendencia, escalados)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {erroGeral !== null && <p className="mt-3.5 text-sm text-perigo">{erroGeral}</p>}

      <DialogFooter className="justify-between">
        <Button
          type="button"
          variant="destructive"
          onClick={() => void handleLimpar()}
          disabled={ocupado}
        >
          {limpar.isPending ? 'Limpando…' : 'Limpar dia'}
        </Button>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" disabled={ocupado}>
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </DialogFooter>

      <AlertDialog
        open={aConfirmar !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setAConfirmar(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠ Conflitos detectados neste dia</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <ol className="m-0 list-decimal pl-5">
                  {criticas.map((pendencia) => (
                    <li key={`${pendencia.colaboradorId}-${pendencia.tipo}`}>
                      {nomePorId.get(pendencia.colaboradorId) ?? '—'}{' '}
                      {fraseDaPendencia(pendencia, escalados)}
                    </li>
                  ))}
                </ol>
                <p className="mt-3">Deseja salvar mesmo assim?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(evento) => {
                evento.preventDefault();
                void handleConfirmarSalvar();
              }}
            >
              Salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
